// Destination city search (aliases and provinces) and who delivers there (coverage).
import type { ConfigRecord } from '@ecobox/schema';

export type City = ConfigRecord<'destinationCity'>;
export type Coverage = ConfigRecord<'coverage'>;
export type Partner = ConfigRecord<'partner'>;

function plain(text: string): string {
  return text.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

/** Active cities that match what was typed: name, province or an alias. Best matches first. */
export function searchCities(cities: readonly City[], text: string, max = 8): City[] {
  const q = plain(text);
  if (q === '') return [];
  const scored: { city: City; score: number }[] = [];
  for (const city of cities) {
    if (!city.active) continue;
    const names = [city.displayName, city.city, ...city.aliases].map(plain);
    let score = 0;
    if (names.some((n) => n === q)) score = 4;
    else if (names.some((n) => n.startsWith(q))) score = 3;
    else if (names.some((n) => n.includes(q))) score = 2;
    else if (plain(`${city.province} ${city.region}`).includes(q)) score = 1;
    if (score > 0) scored.push({ city, score });
  }
  return scored
    .sort((a, b) => b.score - a.score || a.city.displayName.localeCompare(b.city.displayName))
    .slice(0, max)
    .map((s) => s.city);
}

export interface DeliveryInfo {
  mode: 'own' | 'partner' | 'unknown';
  text: string;
}

/** Whether EcoBox delivers to the city itself or a hauler does (12 §12.3). */
export function deliveryInfo(
  cityId: string,
  coverage: ReadonlyMap<string, Coverage>,
  partners: ReadonlyMap<string, Partner>,
): DeliveryInfo {
  const cov = coverage.get(cityId);
  if (!cov) {
    return {
      mode: 'unknown',
      text: 'Nobody is set up to deliver here yet. You can still book it; the office will assign a hauler.',
    };
  }
  if (cov.mode === 'own') {
    return { mode: 'own', text: `EcoBox delivers here itself (from ${cov.servingBranchId}).` };
  }
  const name = cov.partnerId ? partners.get(cov.partnerId)?.name : undefined;
  return {
    mode: 'partner',
    text: `A partner hauler delivers here${name ? ` (${name})` : ''}, in about ${cov.targetDays} days.`,
  };
}
